import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

// import material-ui core
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionActions from '@material-ui/core/AccordionActions';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormGroup from '@material-ui/core/FormGroup';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import FormLabel from '@material-ui/core/FormLabel';

import { TextField } from 'formik-material-ui';

// import material-ui icon
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import TuneOutlinedIcon from '@material-ui/icons/TuneOutlined';
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SortIcon from '@material-ui/icons/Sort';
// import {
//   MuiPickersUtilsProvider,
//   KeyboardTimePicker,
//   KeyboardDatePicker,
// } from '@material-ui/pickers';

// import Formik and Dependency
import { Formik, Form, Field } from 'formik';
import { Select, CheckboxWithLabel } from 'formik-material-ui';

import { server } from './../../../constants';
import { getPeriodDateById, logger, base64 } from './../../../utils/utils';

import { HtmlTooltip, LFTooltip } from './../../fragments/Tooltips';

import SelectPersonalDataCategory from './../SelectPersonalDataCategory';
import { useCategoryGetListByMode } from './../../../hooks/useData';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    minHeight: 20,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  icon: {
    verticalAlign: 'bottom',
    height: 20,
    width: 20,
  },
  // https://stackoverflow.com/questions/53537680/material-ui-auto-complete-rotate-icon-on-expand-or-collapse
  sortDirection: {
    transition: theme.transitions.create(['transform'], {
      duration: theme.transitions.duration.short,
    }),
  },
  sortDirectionAsc: {
    transform: 'rotate(-180deg)',
  },
  sortDirectionDesc: {
    transform: 'rotate(0)',
  },
  details: {
    alignItems: 'center',
  },
  column12: {
    flexBasis: '99.99%',
  },
  column8: {
    flexBasis: '66.66%',
  },
  column4: {
    flexBasis: '33.33%',
  },
}));

/**
 * remArrayItem()
 * Remove item from given array
 * @param {any} item
 * @param {array} arr given array
 * @return {array} array of item removed
 */
function remArrayItem(item, arr) {
  let index = arr.indexOf(item);
  if (index > -1) {
    arr.splice(index, 1);
  }
  return arr;
}

function buildContexts(contexts) {
  let lists = Object.keys(contexts).map((key) =>
    contexts[key].context
      ? `${contexts[key].title}: ${contexts[key].context}`
      : null
  );
  return lists.reduce((acc, curr) => [acc, curr ? ', ' : '', curr]);
}

/**
 * AccordionForm()
 * @param {object} props
 * @param {string} mode staff|reprter default: reporter
 * @param {array} sortsOptions with schema (each option) consist of {name, title}
 * @param {array} onFilterAndSearch Callback function(filters: object, keyword: string, sorts: object, side: string) with
 *                                  @param filters filtering (object) param, consist of {dateStart,dateEnd,category_id,permissionLevels}
 *                                  @param sorts sorting (object) param, consist of {name, direction} (name is a field for sorting)
 *                                  @param keyword isearching (string) param,
 *                                  @param side SS|CS (Server Side | Cliend Side)
 * @return {void}
 */
const AccordionForm = forwardRef(
  (
    {
      mode = server.REQ_PARAM_MODE_REPORTER,
      sortsOptions = [],
      onFilterAndSearch,
    },
    ref
  ) => {
    const classes = useStyles();

    logger('AccordionForm render');

    // get categories for make initail permissionLevel checkLevel when select-all
    let categories = useCategoryGetListByMode('TH', 'staff');

    // issue-filters (if) from catching (localStorage)
    const cacheIfs =
      useMemo(() => {
        let ifs = localStorage.getItem(server.ISSUE_FILTERS_KEY);
        return ifs ? base64.decodeObj(ifs) : {};
      }, [])[mode] || {};

    const sideRef = useRef(cacheIfs.side || 'SS');
    const cacheFilters = useMemo(() => cacheIfs.filters || {}, []);
    const cacheSorts = useMemo(() => cacheIfs.sorts || {}, []);
    const cacheKeyword = useMemo(() => cacheIfs.keyword, []);
    const cacheContexts = useMemo(() => cacheIfs.contexts, []);

    // https://material-ui.com/components/checkboxes/#checkbox (Checkbox with FormControlLabel)

    const initialCanSelectPermissionLevel = cacheFilters.permissionLevels || [
      1,
      2,
      3,
    ];

    const period = getPeriodDateById(cacheFilters.period_id || '1y');

    const initialValues = {
      filters: {
        category_id: cacheFilters.category_id || '*',
        permissionLevels: initialCanSelectPermissionLevel,
        period_id: cacheFilters.period_id || '1y',
        dateStart: period.dateStart,
        dateEnd: period.dateEnd,
      },
      sorts: sortsOptions.length
        ? {
            title: cacheSorts.title || sortsOptions[0].title,
            name: cacheSorts.name || sortsOptions[0].name,
            direction: cacheSorts.direction || 'desc',
          }
        : {},
      keyword: cacheKeyword || '',
      contexts: cacheContexts || {
        category: {
          title: 'ประเภท',
          context: 'ทุกประเภท',
        },
        period: {
          title: 'ช่วงเวลา',
          context: 'ภายใน 1 ปี',
        },
        keyword: {
          title: 'คำค้นหา',
          context: '',
        },
      },
    };

    const [contexts, setContexts] = useState(
      buildContexts(initialValues.contexts)
    );

    // HandleFilterAndSearch(), Set argument and call onFilterAndSearch props
    const handleFilterAndSearch = (values) => {
      if (onFilterAndSearch) {
        let period = getPeriodDateById(values.filters.period_id);

        // filters parameter
        let filters = {
          ...values.filters,
          category_id:
            values.filters.category_id === '*'
              ? undefined
              : values.filters.category_id,
          dateStart: period.startDate,
          dateEnd: period.endDate,
        };

        // sorts parameter
        let sorts = { title: 'dynamic' };
        if (sortsOptions.length) {
          sorts = { ...sorts, ...values.sorts };
        }

        // keyword parameter
        let keyword = values.keyword;

        let contexts = {
          ...values.contexts,
          keyword: { ...values.contexts.keyword, context: keyword },
        };

        let side = sideRef.current;

        /**
         * Test issue-filters in localStorage
         * issue-filters is catching issue filter
         */

        // Read issue-filters  from storage (by ISSUE_FILTERS_KEY)
        let beforeIssueFilters = localStorage.getItem(server.ISSUE_FILTERS_KEY);
        beforeIssueFilters = beforeIssueFilters
          ? base64.decodeObj(beforeIssueFilters)
          : {};

        // Check exist data in object by key reporter|staff (of props mode) & assign {} when data is not exist
        if (!beforeIssueFilters[mode]) {
          beforeIssueFilters[mode] = {};
        }

        // Assign new issue-filters data (of props mode)
        beforeIssueFilters[mode] = {
          ...beforeIssueFilters[`${mode}`],
          filters,
          keyword,
          sorts,
          contexts,
          side,
        };

        // Combile data of each key (reporter|staff) to new issue-filters object
        let newIssueFilters = {
          ...beforeIssueFilters,
        };

        //Set new issue-filters to localStorage
        localStorage.setItem(
          server.ISSUE_FILTERS_KEY,
          base64.encodeObj(newIssueFilters)
        );

        // Process filter and Search
        onFilterAndSearch(filters, keyword, sorts, side);

        // Set search title on acordionForm
        setContexts(buildContexts(contexts));
      }
    };
    // Form (of Formik) reference
    const formikRef = useRef(null);

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        formikRef.current.submitForm();
      },
    }));

    const showFormSearch = ({
      values,
      setFieldValue,
      touched,
      errors,
      submitForm,
    }) => {
      const onCbChange = (e) => {
        let val = Number(e.target.value);
        let newVal = values.filters.permissionLevels.includes(val)
          ? remArrayItem(val, [...values.filters.permissionLevels])
          : [...values.filters.permissionLevels, val];
        setFieldValue('filters.permissionLevels', newVal);
        sideRef.current = 'CS';
        submitForm();
      };
      return (
        <Form autoComplete="off">
          <Accordion square>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1c-content"
              id="panel1c-header"
            >
              <div className={classes.column12}>
                <Box display="flex" alignItems="center">
                  <Box className={classes.column4}>
                    <LFTooltip title="ตั้งค่าการแสดงผล และค้นหาข้อมูลเหตุละเมิด">
                      <Box display="flex" alignItems="center">
                        <TuneOutlinedIcon />
                        <Box mr={0.3}></Box>
                        <Box>
                          {/* <Typography component="div" className={classes.heading}>
                          การค้นหา
                        </Typography> */}
                        </Box>
                      </Box>
                    </LFTooltip>
                  </Box>
                  <Box className={classes.column8}>
                    <Typography
                      component="div"
                      className={classes.secondaryHeading}
                    >
                      {contexts}
                    </Typography>
                  </Box>
                </Box>
              </div>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <div className={classes.column12}>
                <Grid container spacing={1} justify="flex-start">
                  {/* Select category */}
                  <Grid item xs={12} sm={12} md={3}>
                    <Box display="flex" alignItems="center">
                      <Box flexGrow={1} pr={1}>
                        <SelectPersonalDataCategory
                          name="filters.category_id"
                          label="ประเภทของข้อมูลส่วนบุคคล"
                          onChange={(
                            e,
                            { data, permissionLevels },
                            context
                          ) => {
                            // New
                            setFieldValue('contexts.category.context', context);

                            setFieldValue(
                              'filters.permissionLevels',
                              initialCanSelectPermissionLevel
                            );

                            sideRef.current = 'SS';
                            submitForm();
                          }}
                          isSelectAll={true}
                          mode={mode}
                          variant="outlined"
                        />
                      </Box>
                      {mode === server.REQ_PARAM_MODE_STAFF && (
                        <Box>
                          <HtmlTooltip
                            title={
                              <React.Fragment>
                                <Typography>
                                  <Box fontSize={16}>
                                    สิทธิ์การเข้าถึงเหตุละเมิดของท่าน
                                    {categories.map((cate, index) => {
                                      let l = cate.permissionLevel;
                                      return (
                                        <div key={index}>{`ประเภท ${cate.name} ในระดับ ${cate.permissionLevel}`}</div>
                                      );
                                    })}
                                  </Box>
                                </Typography>
                              </React.Fragment>
                            }
                          >
                            <HelpOutlineIcon />
                          </HtmlTooltip>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  {/* Select level */}
                  <Grid item xs={12} sm={12} md={3}>
                    <LFTooltip title="ระดับการดำเนินการจัดการเหตุละเมิด">
                      <FormControl component="fieldset">
                        <FormLabel component="legend" style={{ fontSize: 12 }}>
                          ระดับการดำเนินการ (Level){' '}
                        </FormLabel>
                        <FormGroup row>
                          <Field
                            component={CheckboxWithLabel}
                            type="checkbox"
                            name="filters.permissionLevels"
                            Label={{ label: 'ระดับ 1' }}
                            color="primary"
                            checked={values.filters.permissionLevels.includes(
                              1
                            )}
                            value={1}
                            onChange={onCbChange}
                          />
                          <Field
                            component={CheckboxWithLabel}
                            type="checkbox"
                            name="filters.permissionLevels"
                            Label={{ label: 'ระดับ 2' }}
                            color="primary"
                            checked={values.filters.permissionLevels.includes(
                              2
                            )}
                            value="2"
                            onChange={onCbChange}
                          />
                          <Field
                            component={CheckboxWithLabel}
                            type="checkbox"
                            name="filters.permissionLevels"
                            Label={{ label: 'ระดับ 3' }}
                            color="primary"
                            checked={values.filters.permissionLevels.includes(
                              3
                            )}
                            value="3"
                            onChange={onCbChange}
                          />
                        </FormGroup>
                      </FormControl>
                    </LFTooltip>
                  </Grid>
                  {/* Select date - period  */}
                  <Grid item xs={12} sm={12} md={2}>
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel
                        htmlFor="period_id"
                        style={{ backgroundColor: 'white' }}
                      >
                        ช่วงวันที่แจ้งเหตุ
                      </InputLabel>
                      <Field
                        component={Select}
                        name="filters.period_id"
                        onChange={(e, c) => {
                          let context =
                            typeof c.props.children === 'string'
                              ? c.props.children
                              : '';

                          // New
                          setFieldValue('contexts.period.context', context);

                          setFieldValue('filters.period_id', e.target.value);
                          sideRef.current = 'SS';
                          submitForm();
                        }}
                        inputProps={{
                          id: 'period_id',
                        }}
                      >
                        <MenuItem value="1y">ภายใน 1 ปี</MenuItem>
                        <MenuItem value="3y">ภายใน 3 ปี</MenuItem>
                        <MenuItem value="5y">ภายใน 5 ปี</MenuItem>
                        <MenuItem value="all">ทั้งหมด</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  {sortsOptions.length > 0 && (
                    <Grid container item xs={12} sm={12} md={4}>
                      <Grid
                        item
                        xs={1}
                        style={{ textAlign: 'right', paddingRight: 8 }}
                      >
                        <SortIcon
                          className={clsx([
                            classes.sortDirection,
                            {
                              [classes.sortDirectionAsc]:
                                values.sorts.direction === 'asc',
                              [classes.sortDirectionDesc]:
                                values.sorts.direction === 'desc',
                            },
                          ])}
                        />
                      </Grid>
                      <Grid item xs={7} style={{ paddingRight: 8 }}>
                        <FormControl fullWidth size="small" variant="outlined">
                          <InputLabel
                            htmlFor="sorts-name"
                            style={{ backgroundColor: 'white' }}
                          >
                            เรียงข้อมูลจาก
                          </InputLabel>
                          <Field
                            component={Select}
                            inputProps={{
                              id: 'sorts-name',
                            }}
                            name="sorts.name"
                            onChange={(e, c) => {
                              setFieldValue('sorts.name', e.target.value);
                              setFieldValue('sorts.title', c.props.children);
                              sideRef.current = 'CS';
                              submitForm();
                            }}
                          >
                            {sortsOptions.map((item, index) => (
                              <MenuItem value={item.name} key={index}>
                                {item.title}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl
                          fullWidth
                          size="small"
                          variant="outlined"
                          style={{ marginRight: 8 }}
                        >
                          <InputLabel
                            htmlFor="sorts-direction"
                            style={{ backgroundColor: 'white' }}
                          >
                            รูปแบบการเรียง
                          </InputLabel>
                          <Field
                            component={Select}
                            inputProps={{
                              id: 'sorts-direction',
                            }}
                            name="sorts.direction"
                            onChange={(e) => {
                              setFieldValue('sorts.direction', e.target.value);
                              sideRef.current = 'CS';
                              submitForm();
                            }}
                          >
                            <MenuItem value="asc">น้อยไปมาก</MenuItem>
                            <MenuItem value="desc">มากไปน้อย</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                  {/* Search by keyword */}
                  <Grid item xs={12} sm={12} md={8}>
                    <FormControl fullWidth>
                      <Field
                        component={TextField}
                        name="keyword"
                        id="input-with-icon-textfield"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchOutlinedIcon />
                            </InputAdornment>
                          ),
                        }}
                        label="คำค้นหา"
                        size="small"
                        variant="outlined"
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </div>
            </AccordionDetails>

            <AccordionActions style={{ display: 'none' }}>
              {/* Remain Button type submit take onEnter of input search */}
              <Button
                type="submit"
                size="small"
                color="primary"
                variant="contained"
                onClick={() => {
                  sideRef.current = 'CS';
                }}
              >
                ค้นหา
              </Button>
            </AccordionActions>
          </Accordion>
        </Form>
      );
    };

    return (
      <div className={classes.root}>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitting(false);
            if (onFilterAndSearch) {
              handleFilterAndSearch(values);
            }
          }}
          innerRef={formikRef}
        >
          {(props) => showFormSearch(props)}
        </Formik>
      </div>
    );
  }
);

/**
 * AccordionForm's propTypes
 */
AccordionForm.propTypes = {
  onFilterAndSearch: PropTypes.func.isRequired,
};

/**
 * By default it will only shallowly compare complex objects in the props object.
 * If you want control over the comparison,
 * you can also provide a custom comparison function as the second argument.
 * https://reactjs.org/docs/react-api.html
 * https://stackoverflow.com/questions/63502588/avoid-rerendering-child-when-passing-object-in-props
 */
export default React.memo(AccordionForm, isEqual);
